// apps/web/src/routes/signs/AnnotationModal.tsx
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { X, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AnnotationCanvas } from "./AnnotationCanvas";
import type { AnnotationBoxData } from "./AnnotationBox";
import { fetchAnnotations, saveAnnotations } from "@/features/text-sign/api";
import { api } from "@/lib/api/client";

type Signer = { staffId: number; fullName: string; level: number };

type Props = {
  open: boolean;
  textId: number | null;
  mainFileId: number | null;
  /** Nguồn của mainFileId: "files" khi vừa upload, "attach" khi nạp lại nháp. */
  mainFileSource: "files" | "attach";
  signers: Signer[];
  onClose: () => void;
  onSaved: (count: number) => void;
};

let tempCounter = 0;
const nextTempId = () => `t${++tempCounter}`;

export function AnnotationModal({
  open,
  textId,
  mainFileId,
  mainFileSource,
  signers,
  onClose,
  onSaved,
}: Props) {
  const [boxes, setBoxes] = useState<AnnotationBoxData[]>([]);
  const [fileUrl, setFileUrl] = useState<string | null>(null);

  const signerOptions = signers
    .slice()
    .sort((a, b) => a.level - b.level)
    .map((s) => ({ level: s.level, label: s.fullName }));

  const loaded = useQuery({
    queryKey: ["annotations", textId],
    queryFn: () => fetchAnnotations(textId!),
    enabled: open && !!textId,
  });

  useEffect(() => {
    if (!loaded.data) return;
    setBoxes(
      loaded.data.map((a) => ({
        tempId: nextTempId(),
        id: a.id,
        signLevel: a.signLevel,
        pageIndex: a.pageIndex,
        x: a.x,
        y: a.y,
        w: a.w,
        h: a.h,
      })),
    );
  }, [loaded.data]);

  // Fetch PDF as blob so react-pdf can render it with auth
  useEffect(() => {
    let revoke: string | null = null;
    if (open && mainFileId) {
      // BẮT BUỘC truyền source: id của File ký lấy từ bản nháp là ATTACH_ID, mà
      // endpoint mặc định tra bảng `files` -> 404 -> react-pdf nhận cục JSON lỗi và
      // báo "Không đọc được PDF".
      const srcQ = mainFileSource === "attach" ? "?source=attach" : "";
      api.get(`/file/${mainFileId}/preview${srcQ}`, { responseType: "blob" }).then((res) => {
        const url = URL.createObjectURL(res.data as Blob);
        revoke = url;
        setFileUrl(url);
      });
    }
    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
      setFileUrl(null);
    };
  }, [open, mainFileId, mainFileSource]);

  const save = useMutation({
    mutationFn: () => {
      if (!textId) return Promise.reject(new Error("no text"));
      return saveAnnotations(
        textId,
        boxes.map(({ signLevel, pageIndex, x, y, w, h }) => ({
          signLevel,
          pageIndex,
          x,
          y,
          w,
          h,
        })),
      );
    },
    onSuccess: (r) => {
      onSaved(r.count);
      onClose();
    },
  });

  if (!open || !textId || !mainFileId) return null;

  // Group coverage: which levels have at least one box
  const coveredLevels = new Set(boxes.map((b) => b.signLevel));
  const missing = signerOptions.filter((s) => !coveredLevels.has(s.level));

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex flex-col">
      <div className="flex items-center justify-between px-5 py-3 bg-background border-b border-border">
        <h3 className="font-semibold">Gắn vị trí ký</h3>
        <button
          onClick={onClose}
          className="h-8 w-8 grid place-items-center rounded hover:bg-muted"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex-1 flex min-h-0">
        <aside className="w-64 border-r border-border bg-background p-4 overflow-y-auto">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
            Danh sách ký ({signers.length})
          </div>
          <ul className="space-y-1 text-sm">
            {signerOptions.map((s) => (
              <li
                key={s.level}
                className={`px-2 py-1.5 rounded flex items-center justify-between ${
                  coveredLevels.has(s.level)
                    ? "bg-success/10 text-success"
                    : "bg-warning/10 text-warning"
                }`}
              >
                <span>
                  Cấp {s.level} — {s.label}
                </span>
                <span className="text-xs">
                  {boxes.filter((b) => b.signLevel === s.level).length} ô
                </span>
              </li>
            ))}
          </ul>
          {missing.length > 0 && (
            <div className="mt-3 text-xs text-warning">
              ⚠ Thiếu: {missing.map((m) => `Cấp ${m.level}`).join(", ")}
            </div>
          )}
        </aside>
        <main className="flex-1 overflow-auto bg-neutral-800 p-4">
          {fileUrl && (
            <AnnotationCanvas
              fileUrl={fileUrl}
              boxes={boxes}
              signerOptions={signerOptions}
              onAdd={(_, b) => setBoxes((prev) => [...prev, { ...b, tempId: nextTempId() }])}
              onChangeBox={(b) =>
                setBoxes((prev) => prev.map((x) => (x.tempId === b.tempId ? b : x)))
              }
              onDeleteBox={(id) => setBoxes((prev) => prev.filter((x) => x.tempId !== id))}
            />
          )}
        </main>
      </div>
      <div className="flex items-center justify-between px-5 py-3 bg-background border-t border-border">
        <div className="text-sm text-muted-foreground">
          {boxes.length} ô · {coveredLevels.size}/{signers.length} signer được phủ
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="md" onClick={onClose}>
            Huỷ
          </Button>
          <Button
            variant="default"
            size="md"
            onClick={() => save.mutate()}
            disabled={save.isPending}
          >
            <Save className="h-4 w-4 mr-1.5" />
            {save.isPending ? "Đang lưu…" : "Lưu vị trí ký"}
          </Button>
        </div>
      </div>
    </div>
  );
}
